import { useId, useState } from 'react';

import { monthlyPrice, PRICING, rateFor } from './content.js';

/**
 * Pricing, with a calculator.
 *
 * The table alone makes a benefits manager do arithmetic before they know
 * whether this is worth a meeting, and a per-employee rate with a floor and
 * five bands is exactly the kind of sum people get wrong and then quote back
 * to you. The calculator answers the only question they have.
 *
 * It calls the same `monthlyPrice` the rest of the site would, so the figure
 * on screen cannot drift from the table beside it — both are rendered from
 * `PRICING`.
 */

const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const whole = new Intl.NumberFormat('en-US');

/** "100–1,000" or "4,001+", from the band itself. */
function bandLabel(from: number, to: number | null): string {
  return to === null ? `${whole.format(from)}+` : `${whole.format(from)}–${whole.format(to)}`;
}

export function Pricing() {
  const inputId = useId();
  const [employees, setEmployees] = useState(250);

  const monthly = monthlyPrice(employees);
  const atMinimum = employees * rateFor(employees) < PRICING.minimumMonthly;

  return (
    <section className="pricing" id="pricing" aria-labelledby="pricing-title">
      <div className="pricing__intro" data-reveal>
        <p className="kicker">Pricing</p>
        <h2 id="pricing-title">A dollar a person. Less as you grow.</h2>
        <p>
          Per employee, per month, billed monthly. Every part of the programme is
          included — the employee app, the seven wellness areas, monthly challenges,
          and the employer console.
        </p>
        {/*
          The operator's reasoning, in his words and kept to what we can speak
          for: our own costs. It says nothing about what any competitor spends,
          because we do not know that and would be guessing in public.
        */}
        <p className="pricing__why">
          <strong>Why it costs less than you expect.</strong> Platforms in this category
          are still paying off years of engineering and a large sales organisation.
          This one was built by a small team with frontier AI, without that bill —
          so it is not in your price.
        </p>
      </div>

      <div className="pricing__calc" data-reveal>
        <label htmlFor={inputId}>How many employees are eligible?</label>
        <div className="pricing__calc-row">
          <input
            id={inputId}
            type="number"
            min={1}
            max={100000}
            step={25}
            value={employees}
            onChange={(event) => setEmployees(Math.max(0, Number(event.target.value) || 0))}
            inputMode="numeric"
          />
          <p className="pricing__result" aria-live="polite">
            <strong>{dollars.format(monthly)}</strong>
            <span>per month</span>
          </p>
        </div>
        <p className="pricing__calc-note">
          {atMinimum
            ? `Below ${whole.format(PRICING.minimumMonthly)} employees the ${dollars.format(PRICING.minimumMonthly)} monthly minimum applies.`
            : `${whole.format(Math.max(0, Math.floor(employees)))} × ${dollars.format(rateFor(employees))} per employee.`}
        </p>
      </div>

      <table className="pricing__table" data-reveal>
        <caption>Rate per employee, per month</caption>
        <thead>
          <tr>
            <th scope="col">Employees</th>
            <th scope="col">Rate</th>
          </tr>
        </thead>
        <tbody>
          {PRICING.bands.map((band) => (
            <tr key={band.from} data-current={employees >= band.from && (band.to === null || employees <= band.to) ? 'true' : 'false'}>
              <th scope="row">{bandLabel(band.from, band.to)}</th>
              <td>{dollars.format(band.rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="pricing__floor" data-reveal>
        Minimum {dollars.format(PRICING.minimumMonthly)} per month.
      </p>
    </section>
  );
}
